import React, { useEffect, useState } from "react";
import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { nameMinLength, nameMaxLength, getLocale } from "../../../../common/parameters";
import deleteUndefined from "../../../../common/helpers/deleteUndefined";
import getName from "../../../../common/helpers/getName";

import routing from "../../../routing";
import { renamePlan, deletePlan, duplicatePrivatePlan } from "../../../store/pack/actions";
import { increasePackTotalPlan, decreasePackTotalPlan } from "../../../store/dashboard/actions";
import { IStateTree } from "../../../store/interfaces";
import { post } from "../../../actions/general";
import { ISelectEvent } from "../../../types";

import Form, { Title, Submit, Cancel, Select } from "../../Form";
import LimitReached from "../../../pages/dashboard/Pack/LimitReached";
import Modal from "../../Modal";
import CloneModal from "../../CloneModal";
import ViewSharedLabel from "../../ViewSharedLabel";

import RenamePlan from "./RenamePlan";
import Report from "./Report";
import ChangeDateModal from "./ChangeDateModal";
import ChangeDescriptionModal from "./ChangeDescriptionModal";

import { DropdownOpener, StyledDropdown } from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import { ISolfanameData } from "../../../../common/types";
import Icon from "../../../icon";

const getCasedMessage = (message: string) => {
    return message.slice(0, 1).toUpperCase() + message.slice(1);
};

const Dropdown = ({
    plan,
    isBrowse,
    isHomework,
    modifyHomeworkDate,
    modifyHomeworkDescription,
    renameHomework,
    index,
    deleteHomework,
}: IProps) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { packs, pack } = useSelector((state: IStateTree) => state)
    const { formatMessage, locale } = intl;
    const navigate = useNavigate();

    const [validationPlanName, setValidationPlanName] = useState({});
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [dropdownShow, setDropdownShow] = useState(false);
    const [renameName, setRenameName] = useState<{[language: string]: string}>({});
    const [renameModal, setRenameModal] = useState(false);
    const [changeDescriptionModal, setChangeDescriptionModal] = useState(false);
    const [areYouSureModal, setAreYouSureModal] = useState(false);
    const [reportPlan, setReportPlan] = useState(false);
    const [limit, setLimit] = useState<null | number>(null)
    const [moveModal, setMoveModal] = useState(false);
    const [selectedMovePack, setSelectedMovePack] = useState<null | string>(null);
    const [movePackValidation, setMovePackValidation] = useState<string | null>(null);
    const [changeDateModal, setChangeDateModal] = useState(false);
    const [loadingRenamePlan, setLoadingRenamePlan] = useState(false);
    const [loadingMovePlan, setLoadingMovePlan] = useState(false);
    const [loadingDeletePlan, setLoadingDeletePlan] = useState(false);

    const clickOutside = () => {
        setDropdownShow(false);
        window.removeEventListener("click", clickOutside);
    };
    const handleToggleDropdown = () => {
        setDropdownShow(old => !old);
        if (!dropdownShow) {
            setTimeout(() => {
                window.addEventListener("click", clickOutside);
            });
        } else {
            window.removeEventListener("click", clickOutside);
        }
    };
    
    useEffect(() => {
        return () => {
            window.removeEventListener("click", clickOutside);
        }
    }, [])
    
    const handleRenamePlan = () => {
        let newName = renameName;

        if (newName[getLocale(locale)].length === 0) {
            setValidationPlanName({ [getLocale(locale)]: "required" })
        } else if (newName[getLocale(locale)].length < nameMinLength) {
            setValidationPlanName({ [getLocale(locale)]: "tooShortPlanName" })
        } else if (nameMaxLength < newName[getLocale(locale)].length) {
            setValidationPlanName({ [getLocale(locale)]: "tooLongPlanName" })
        } else {
            setLoadingRenamePlan(true);
            if (isHomework) {
                renameHomework!(plan._id, newName[getLocale(locale)], success => {
                    setRenameModal(!success)
                    setLoadingRenamePlan(false);
                });
            } else {
                dispatch(renamePlan({ planId: plan._id, name: deleteUndefined(newName) }, (success: boolean) => {
                    setRenameModal(!success);
                    setLoadingRenamePlan(false);
                }));
            }
        }
    };

    const handleRenameChange = (e: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setRenameName(old => ({
            ...old,
            [name]: value,
        }))
        setValidationPlanName(old => ({
            ...old,
            [name]: null,
        }));
    };

    const handleDuplicate = () => {
        dispatch(duplicatePrivatePlan({ planId: plan._id, packId: pack._id! }, (res: { data: { limit?: number }}) => {
            if (!res.data.limit) {
                dispatch(increasePackTotalPlan(pack._id!));
            }
            window.scrollTo(0, 1000000);
        }));
    };

    const handleFinishClonePlan = ({ name, selectedPack }: { name: string, selectedPack: string }) => {
        dispatch(post("clone-plan", { _id: plan._id, name, selectedPack }, (res: { data: { limit?: number, _id: string }}) => {
            const { data } = res;
            if (data.limit) {
                setLimit(data.limit);
                setShowCloneModal(false);
            } else {
                const { _id } = data;
                navigate("/" + routing.plan + "/" + _id);
            }
        }));
    };

    const handleCloseCloneModal = () => {
        setShowCloneModal(false);
    };

    const handleDeletePlan = () => {
        setLoadingDeletePlan(true);
        if (isHomework) {
            deleteHomework!(() => {
                setLoadingDeletePlan(false);
            });
        } else {
            dispatch(deletePlan({ planId: plan._id, index: index! }, () => {
                dispatch(decreasePackTotalPlan(pack._id!));
            }));
        }
    };
    const handleMoveTest = () => {
        if (!selectedMovePack) {
            setMovePackValidation("required");
            return;
        }
        setLoadingMovePlan(true);
        dispatch(post(
            "move-plan",
            { planId: plan._id, packId: selectedMovePack },
            () => {
                dispatch(decreasePackTotalPlan(pack._id!));
                dispatch(increasePackTotalPlan(selectedMovePack));
                navigate("/" + routing.pack + "/" + selectedMovePack);
                setLoadingMovePlan(false);
            },
            () => {
                setLoadingMovePlan(false);
            }
        ));
    };
    const handleChangeMovePackSelect = (e: ISelectEvent) => {
        setSelectedMovePack(e.target.value)
        setMovePackValidation(null);
    };

    return (
        <>
            <DropdownOpener onClick={handleToggleDropdown}>
                <svg width="30" height="30">
                    <circle r="2.5" cy="8" cx="15" />
                    <circle r="2.5" cy="15" cx="15" />
                    <circle r="2.5" cy="22" cx="15" />
                </svg>
                <StyledDropdown className={cn(dropdownShow && "Show")}>
                    {!isBrowse && (
                        <div
                            onClick={() => {
                                setRenameModal(true)
                                setRenameName(isHomework
                                    ? { [getLocale(locale)]: (plan as any).name as string }
                                    : { [getLocale(locale)]: getName(plan.name, intl), ...plan.name }
                                )
                            }}
                        >
                            <Icon i="rename" />
                            <FormattedMessage {...messages.rename} />
                        </div>
                    )}
                    {!isBrowse && !isHomework && (
                        <div
                            onClick={() => {
                                if (packs.packs!.length !== 1) {
                                    setMoveModal(true);
                                    setSelectedMovePack(plan.pack!);
                                }
                            }}
                            className={cn(packs.packs!.length === 1 && "Disabled")}
                        >
                            <Icon i="move-folder" />
                            <FormattedMessage {...messages.move} />
                        </div>
                    )}
                    {(!isBrowse || (isBrowse && !plan.copyright)) && !isHomework && (
                        <div
                            onClick={() => {
                                if (isBrowse) {
                                    setShowCloneModal(true);
                                } else {
                                    handleDuplicate();
                                }
                            }}
                        >
                            <Icon i="duplicate" />
                            <FormattedMessage {...messages.duplicate} />
                        </div>
                    )}
                    {plan.shared && !isHomework && (
                        <Link to={"/" + routing.publicPlan + "/" + plan.publicId}>
                            <Icon i={plan.accepted || plan.public ? "browse" : "meta"} />
                            <ViewSharedLabel plan={plan} />
                        </Link>
                    )}
                    {isHomework && (
                        <div
                            onClick={() => {
                                setChangeDescriptionModal(true);
                            }}
                        >
                            <Icon i="messages" />
                            {getCasedMessage(formatMessage(messages.modifySomething, {
                                value: formatMessage(messages.homeworkDescription),
                            }).toLowerCase())}
                        </div>
                    )}
                    {isHomework && (
                        <div
                            onClick={() => {
                                setChangeDateModal(true);
                            }}
                        >
                            <Icon i="alert" />
                            <FormattedMessage {...messages.changeDate} />
                        </div>
                    )}
                    {!isBrowse && (
                        <div
                            onClick={() => {
                                setAreYouSureModal(true);
                            }}
                        >
                            <Icon i="trash" />
                            <FormattedMessage {...messages.delete} />
                        </div>
                    )}
                    {isBrowse && !isHomework && (
                        <div
                            onClick={() => {
                                setReportPlan(true);
                            }}
                        >
                            <Icon i="alert" />
                            <FormattedMessage {...messages.report} />
                        </div>
                    )}
                </StyledDropdown>
            </DropdownOpener>
            {renameModal && (
                <RenamePlan
                    close={() => setRenameModal(false)}
                    changeName={handleRenameChange}
                    rename={handleRenamePlan}
                    planName={renameName}
                    validationPlanName={validationPlanName}
                    isHomework={isHomework}
                    loading={loadingRenamePlan}
                />
            )}

            {areYouSureModal && (
                <Modal close={() => {
                    setAreYouSureModal(false);
                }}>
                    <Title>
                        {!isHomework && <FormattedMessage {...messages.deleteTitle} />}
                        {isHomework && <FormattedMessage {...messages.deleteHomeworkTitle} />}
                    </Title>
                    <Form disabled={loadingDeletePlan}>
                        <p>
                            {!isHomework && <FormattedMessage {...messages.deleteQuestion} />}
                            {isHomework && <FormattedMessage {...messages.deleteHomeworkText} />}
                        </p>
                        <p>{isHomework ? (plan.name as unknown as string) : getName(plan.name, intl)}</p>
                        <Submit onClick={handleDeletePlan} loading={loadingDeletePlan}>
                            <FormattedMessage {...messages.delete} />
                        </Submit>
                        <Cancel onClick={() => {
                            setAreYouSureModal(false);
                        }}>
                            <FormattedMessage {...messages.cancel} />
                        </Cancel>
                    </Form>
                </Modal>
            )}
            {moveModal && (
                <Modal close={() => {
                    setMoveModal(false);
                }}>
                    <Title>
                        <FormattedMessage {...messages.moveTitle} />
                    </Title>
                    <Form disabled={loadingMovePlan}>
                        <Select
                            name="pack"
                            disabled={loadingMovePlan}
                            label={formatMessage(messages.pack)}
                            options={packs.packs!
                                .map((pack: any) => {
                                    return { value: pack._id, label: pack.name };
                                })
                                .filter((p: any) => {
                                    if (p.value === pack._id) return false;
                                    else return true;
                                })}
                            onChange={handleChangeMovePackSelect}
                            value={selectedMovePack || undefined}
                            noDelete
                            validation={movePackValidation}
                        />
                        <Submit onClick={handleMoveTest} loading={loadingMovePlan}>
                            <FormattedMessage {...messages.move} />
                        </Submit>
                        <Cancel onClick={() => {
                            setMoveModal(false);
                        }}>
                            <FormattedMessage {...messages.cancel} />
                        </Cancel>
                    </Form>
                </Modal>
            )}
            {changeDateModal && (
                <ChangeDateModal
                    date={new Date(plan.date!)}
                    startDate={new Date(plan.startDate!)}
                    close={() => {
                        setChangeDateModal(false);
                    }}
                    modifyHomeworkDate={modifyHomeworkDate!}
                />
            )}
            {changeDescriptionModal && (
                <ChangeDescriptionModal
                    description={(plan as any).description as string}
                    close={() => {
                        setChangeDescriptionModal(false);
                    }}
                    modifyHomeworkDescription={modifyHomeworkDescription!}
                />
            )}
            {reportPlan && (
                <Report planId={plan._id} close={() => {
                    setReportPlan(false);
                }} />
            )}
            {!!limit && limit > 0 && (
                <Modal close={() => {
                    setLimit(null);
                }}>
                    <LimitReached limit={limit} close={() => {
                    setLimit(null);
                }} />
                </Modal>
            )}
            {showCloneModal && (
                <CloneModal close={handleCloseCloneModal} name={plan.name} submit={handleFinishClonePlan} />
            )}
        </>
    );
}

export default Dropdown;
